import { useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ClipboardList, Plus, Check, X, Edit2, ExternalLink, StickyNote } from "lucide-react";
import { useBoardTasks, useUpdateBoardTask, useCreateBoardTask } from "@/hooks/useBoardTasks";
import { useCourseNotesAsBoardTasks } from "@/hooks/useCourseNotes";
import { PlanningTaskDialog } from "@/components/admin/planning/PlanningTaskDialog";
import type { BoardTask } from "@/hooks/useBoardTasks";
import { toast } from "sonner";

const COURSE_BASE = "/admin/dashboard/kurse/";

/** Extract the course base path if the current URL is a course editor page */
function getCourseBasePath(pathname: string): string | null {
  const match = pathname.match(/^(\/admin\/dashboard\/kurse\/[a-f0-9-]+)/);
  return match ? match[1] : null;
}

/** Extract course ID from a linked_page_path */
function extractCourseId(path: string): string | null {
  const match = path.match(/\/admin\/dashboard\/kurse\/([a-f0-9-]+)/);
  return match ? match[1] : null;
}

export function FloatingTaskButton() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: tasks = [] } = useBoardTasks();
  const { data: courseNotes = [] } = useCourseNotesAsBoardTasks();
  const updateTask = useUpdateBoardTask();
  const createTask = useCreateBoardTask();
  const [open, setOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<BoardTask | null>(null);

  const isOnPlanungszentrale = location.pathname.includes("planungszentrale");

  // Full path including search params (for storing with task)
  const fullPath = location.pathname + location.search;

  // Course-aware matching
  const courseBasePath = getCourseBasePath(location.pathname);

  // Merge board tasks + course notes for display
  const allTasks = useMemo(() => [...tasks, ...courseNotes], [tasks, courseNotes]);

  const linkedTasks = useMemo(() => {
    return allTasks.filter((t) => {
      if (!t.linked_page_path) return false;
      if (courseBasePath) {
        return t.linked_page_path.startsWith(courseBasePath);
      }
      return location.pathname.startsWith(t.linked_page_path.split("?")[0]);
    });
  }, [allTasks, location.pathname, courseBasePath]);

  const openTasks = linkedTasks.filter((t) => t.status !== "done" && t.status !== "erledigt");

  if (isOnPlanungszentrale) return null;

  /** Set a course's import_status */
  const setCourseStatus = async (courseId: string, status: string) => {
    await supabase
      .from("courses")
      .update({ import_status: status } as Record<string, unknown>)
      .eq("id", courseId);
  };

  /** Check if all tasks for a course are done, if so set to fertiggestellt */
  const checkAndUpdateCourseStatus = async (courseId: string) => {
    const courseTasks = tasks.filter(
      (t) => t.linked_page_path && extractCourseId(t.linked_page_path) === courseId
    );
    const allDone = courseTasks.every((t) => t.status === "done");
    if (allDone) {
      await setCourseStatus(courseId, "fertiggestellt");
      toast.success("Alle Aufgaben erledigt - Kurs auf Fertiggestellt gesetzt");
    }
  };

  const isCourseNote = (task: BoardTask): boolean => {
    return task.id.startsWith("cn_");
  };

  const handleMarkDone = async (task: BoardTask) => {
    if (isCourseNote(task)) {
      // Update course_notes directly
      const realId = task.id.replace("cn_", "");
      const { error } = await supabase
        .from("course_notes")
        .update({ status: "erledigt", completed_at: new Date().toISOString() })
        .eq("id", realId);
      if (error) {
        toast.error("Fehler beim Aktualisieren");
        return;
      }
      queryClient.invalidateQueries({ queryKey: ["course-notes-as-board-tasks"] });
      // Check if all course tasks are now done
      const courseId = task.linked_page_path ? extractCourseId(task.linked_page_path) : null;
      if (courseId) {
        const courseTasks = allTasks.filter(
          (t) => t.linked_page_path && extractCourseId(t.linked_page_path) === courseId
        );
        const allDoneNow = courseTasks.every((t) => t.id === task.id || t.status === "done" || t.status === "erledigt");
        if (allDoneNow) {
          await setCourseStatus(courseId, "fertiggestellt");
          toast.success("Alle Aufgaben erledigt - Kurs auf Fertiggestellt gesetzt");
        }
      }
      return;
    }

    updateTask.mutate({ id: task.id, status: "done" }, {
      onSuccess: () => {
        const courseId = task.linked_page_path ? extractCourseId(task.linked_page_path) : null;
        if (courseId) {
          const courseTasks = allTasks.filter(
            (t) => t.linked_page_path && extractCourseId(t.linked_page_path) === courseId
          );
          const allDoneNow = courseTasks.every((t) => t.id === task.id || t.status === "done" || t.status === "erledigt");
          if (allDoneNow) {
            setCourseStatus(courseId, "fertiggestellt");
            toast.success("Alle Aufgaben erledigt - Kurs auf Fertiggestellt gesetzt");
          }
        }
      },
    });
  };

  const handleSave = (data: Record<string, unknown>) => {
    if (editingTask) {
      updateTask.mutate({ id: editingTask.id, ...data }, {
        onSuccess: () => {
          setDialogOpen(false);
          setEditingTask(null);
        },
      });
    } else {
      createTask.mutate({ ...data, linked_page_path: fullPath } as BoardTask & { title: string }, {
        onSuccess: () => {
          setDialogOpen(false);
          // Auto-set course status to klaerungsbedarf
          const courseId = courseBasePath ? extractCourseId(courseBasePath) : null;
          if (courseId) {
            setCourseStatus(courseId, "klaerungsbedarf");
          }
        },
      });
    }
  };

  const handleNavigateToTask = (task: BoardTask) => {
    if (!task.linked_page_path) return;
    const [pathname, search] = task.linked_page_path.split("?");
    if (pathname === location.pathname && search) {
      navigate(`${pathname}?${search}`, { replace: true });
    } else {
      navigate(task.linked_page_path);
    }
  };

  const isTaskOnDifferentPage = (task: BoardTask): boolean => {
    if (!task.linked_page_path) return false;
    return task.linked_page_path !== fullPath;
  };

  return (
    <>
      {/* FAB */}
      <div className="fixed bottom-6 right-6 z-50">
        <Button
          size="icon"
          className="h-14 w-14 rounded-full shadow-lg relative"
          onClick={() => setOpen(!open)}
        >
          <ClipboardList className="h-6 w-6" />
          {openTasks.length > 0 && (
            <Badge className="absolute -top-1 -right-1 h-5 w-5 p-0 flex items-center justify-center text-[10px] bg-destructive text-destructive-foreground">
              {openTasks.length}
            </Badge>
          )}
        </Button>
      </div>

      {/* Panel */}
      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-80 max-h-[60vh] overflow-y-auto">
          <Card className="shadow-xl border-2">
            <CardHeader className="p-3 pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm">
                {courseBasePath ? "Aufgaben für diesen Kurs" : "Aufgaben für diese Seite"}
              </CardTitle>
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => {
                    setEditingTask(null);
                    setDialogOpen(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
                <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setOpen(false)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0 space-y-2">
              {linkedTasks.length === 0 ? (
                <p className="text-xs text-muted-foreground py-4 text-center">
                  Keine Aufgaben {courseBasePath ? "für diesen Kurs" : "für diese Seite"}
                </p>
              ) : (
                linkedTasks.map((t) => {
                  const isNote = isCourseNote(t);
                  const isDone = t.status === "done" || t.status === "erledigt";
                  return (
                    <div
                      key={t.id}
                      className={`flex items-start gap-2 p-2 rounded border text-sm ${
                        isDone ? "opacity-50" : ""
                      } ${isNote ? "border-l-2 border-l-amber-400" : ""}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          {isNote && <StickyNote className="h-3 w-3 shrink-0 text-amber-500" />}
                          <p className={isDone ? "line-through" : ""}>{t.title}</p>
                        </div>
                        {isTaskOnDifferentPage(t) && (
                          <button
                            onClick={() => handleNavigateToTask(t)}
                            className="text-[10px] text-primary hover:underline flex items-center gap-0.5 mt-0.5"
                          >
                            <ExternalLink className="h-2.5 w-2.5" />
                            Zur Lektion
                          </button>
                        )}
                      </div>
                      <div className="flex gap-0.5">
                        {!isDone && (
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleMarkDone(t)}>
                            <Check className="h-3 w-3 text-green-600" />
                          </Button>
                        )}
                        {!isNote && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6"
                            onClick={() => {
                              setEditingTask(t);
                              setDialogOpen(true);
                            }}
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog */}
      <PlanningTaskDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditingTask(null);
        }}
        task={editingTask}
        onSave={handleSave}
        isPending={createTask.isPending || updateTask.isPending}
        defaultStatus="todo"

      />
    </>
  );
}
